import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useAdminEthicsReportApi from './config';
import { parseTableParams } from '../../utils/Utils';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import { selectFilterOptions } from '../../utils/selectFilterColumn';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '18'};
  color: ${({ $color }) => $color};
`;

const PriorityBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '18'};
  color: ${({ $color }) => $color};
`;

const statusColors = {
  0: '#3B82F6', 1: '#8B5CF6', 2: '#F59E0B', 3: '#EF4444',
  4: '#10B981', 5: '#6B7280', 6: '#9CA3AF', 7: '#F97316',
};

const priorityColors = {
  0: '#6B7280', 1: '#F59E0B', 2: '#EF4444', 3: '#DC2626',
};

// Backend EthicsReportDataAccess.UnassignedFilterValue ile aynı.
const UNASSIGNED = 'unassigned';

export default function EthicsReportsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const { fetching, data, pagination, categories, assignees } = useAdminEthicsReportApi(queryParams);

  const statusLabels = useMemo(() => ({
    0: t('ethicsReport.statusNew'),
    1: t('ethicsReport.statusAcknowledged'),
    2: t('ethicsReport.statusUnderReview'),
    3: t('ethicsReport.statusInvestigation'),
    4: t('ethicsReport.statusActionTaken'),
    5: t('ethicsReport.statusClosed'),
    6: t('ethicsReport.statusUnfounded'),
    7: t('ethicsReport.statusAwaitingInfo'),
  }), [t]);

  const priorityLabels = useMemo(() => ({
    0: t('ethicsReport.priorityLow'),
    1: t('ethicsReport.priorityMedium'),
    2: t('ethicsReport.priorityHigh'),
    3: t('ethicsReport.priorityCritical'),
  }), [t]);

  const categoryLabel = useCallback(
    (code, name) => (code ? t(`ethicsReport.category_${code}`, { defaultValue: name }) : name || '-'),
    [t],
  );

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: categoryLabel(c.code, c.name) })),
    [categories, categoryLabel],
  );
  const statusOptions = useMemo(
    () => Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
    [statusLabels],
  );
  const priorityOptions = useMemo(
    () => Object.entries(priorityLabels).map(([value, label]) => ({ value, label })),
    [priorityLabels],
  );
  const assigneeOptions = useMemo(
    () => [
      { value: UNASSIGNED, label: t('ethicsReport.unassigned') },
      ...assignees.map((a) => ({ value: a.id, label: a.name || '-' })),
    ],
    [assignees, t],
  );

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'trackingCode', label: t('ethicsReport.trackingCode'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'title', label: t('ethicsReport.reportTitle'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'categoryName',
      label: t('ethicsReport.category'),
      options: {
        filter: true, sort: true,
        ...selectFilterOptions({ label: t('ethicsReport.category'), options: categoryOptions, filterKey: 'categoryId[in]', multiple: true }),
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return categoryLabel(row?.categoryCode, row?.categoryName);
        },
      },
    },
    {
      name: 'status',
      label: t('ethicsReport.status'),
      options: {
        filter: true, sort: true,
        ...selectFilterOptions({ label: t('ethicsReport.status'), options: statusOptions, filterKey: 'status[in]', multiple: true }),
        customBodyRender: (v) => (
          <StatusBadge $color={statusColors[v] || '#6B7280'}>
            {statusLabels[v] || '-'}
          </StatusBadge>
        ),
      },
    },
    {
      name: 'priority',
      label: t('ethicsReport.priority'),
      options: {
        filter: true, sort: true,
        ...selectFilterOptions({ label: t('ethicsReport.priority'), options: priorityOptions, filterKey: 'priority[in]', multiple: true }),
        customBodyRender: (v) => (
          <PriorityBadge $color={priorityColors[v] || '#6B7280'}>
            {priorityLabels[v] || '-'}
          </PriorityBadge>
        ),
      },
    },
    {
      name: 'assignedToName',
      label: t('ethicsReport.assignedTo'),
      options: {
        filter: true, sort: false,
        ...selectFilterOptions({ label: t('ethicsReport.assignedTo'), options: assigneeOptions, filterKey: 'assignedTo[in]', multiple: true }),
        customBodyRender: (v) => v || '-',
      },
    },
    dateFilterColumn('createDate', t('ethicsReport.date'), lang),
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return (
            <OptionsMenu
              actions={[
                { type: 'details', label: t('ethicsReport.detail'), onClick: () => history.push(`/ethics-reports/${row.id}`) },
              ]}
              row={row}
            />
          );
        },
      },
    },
  ], [t, lang, data, history, statusLabels, priorityLabels, categoryLabel, categoryOptions, statusOptions, priorityOptions, assigneeOptions]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/ethics-reports/${row.id}`);
  }, [history]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('ethicsReport.adminTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange, handleRowClick])}
    </Container>
  );
}
